import { StyledH2 } from "@aureskonnect/react-ui";
import React from "react";
import { useSnapshot } from "valtio";
import { useTranslation } from "react-i18next";
import { Card, Input, Label } from "reactstrap";
import { store as device, setIsEdited } from "./City/store";
import {
    store as zoneStore,
    setIsEdited as setIsEditedZone,
} from "./Zone/store";
import {
    store as cityAmountStore,
    setIsEdited as setIsEditedCityAmount,
} from "./CityAmount/store";
import {
    store as zoneAmountStore,
    setIsEdited as setIsEditedZoneAmount,
} from "./ZoneAmount/store";
import { ConfirmationUpdatingDataMessageModal } from "@components/Common/ConfirmationUpdatingDataMessageModal";
import {
    setIsAddCity,
    setIsAddZone,
    setIsAddZoneAmount,
    setIsAddCityAmount,
    setOrders,
    store as shopStore,
} from "../store";
import City from "./City";
import Zone from "./Zone";
import ZoneAmount from "./ZoneAmount";
import CityAmount from "./CityAmount";

type DeliveryMode = "city" | "zone" | "zoneAmount" | "cityAmount";

const RADIO_OPTIONS: { mode: DeliveryMode; label: string }[] = [
    { mode: "city", label: "Delivery management by city" },
    { mode: "cityAmount", label: "Delivery management by city + order amount" },
    { mode: "zone", label: "Delivery management by zone (in kilometers)" },
    {
        mode: "zoneAmount",
        label: "Delivery management by zone (in km) + order amount",
    },
];

export default function Delivery({ selectedRows }: any) {
    const { t } = useTranslation();
    const { isEdited } = useSnapshot(device);
    const { isEdited: isEditedZone } = useSnapshot(zoneStore);
    const { isEdited: isEditedCityAmount } = useSnapshot(cityAmountStore);
    const { isEdited: isEditedZoneAmount } = useSnapshot(zoneAmountStore);
    const { orders } = useSnapshot(shopStore);

    const anyFormEdited =
        isEdited || isEditedZone || isEditedCityAmount || isEditedZoneAmount;

    const [showConfirmation, setShowConfirmation] = React.useState(false);
    const pendingActionRef = React.useRef<() => void>(() => {});

    const getInitialMode = React.useCallback((): DeliveryMode => {
        const dataPrice = orders?.dataPrice;
        if (dataPrice?.LivraisonzoneMontant) return "zoneAmount";
        if (dataPrice?.LivraisonVilleMontant) return "cityAmount";
        if (dataPrice?.Livraisonzone) return "zone";
        return "city";
    }, [orders?.dataPrice]);

    const [deliveryMode, setDeliveryMode] = React.useState<DeliveryMode>(
        getInitialMode
    );

    React.useEffect(() => {
        setDeliveryMode(getInitialMode());
    }, [getInitialMode]);

    const updateDeliveryFlags = React.useCallback(
        (mode: DeliveryMode) => {
            const updatedDataPrice = {
                ...(orders?.dataPrice || {}),
                LivraisonVille: mode === "city",
                Livraisonzone: mode === "zone",
                LivraisonzoneMontant: mode === "zoneAmount",
                LivraisonVilleMontant: mode === "cityAmount",
            };
            setOrders({ dataPrice: updatedDataPrice });
        },
        [orders?.dataPrice]
    );

    const resetAddFlags = React.useCallback(() => {
        setIsAddCity(false);
        setIsAddZone(false);
        setIsAddZoneAmount(false);
        setIsAddCityAmount(false);
    }, []);

    const resetAllEdited = React.useCallback(() => {
        setIsEdited(false);
        setIsEditedZone(false);
        setIsEditedCityAmount(false);
        setIsEditedZoneAmount(false);
    }, []);

    const restoreAllEdited = React.useCallback(
        (value: boolean) => {
            if (isEdited) setIsEdited(value);
            if (isEditedZone) setIsEditedZone(value);
            if (isEditedCityAmount) setIsEditedCityAmount(value);
            if (isEditedZoneAmount) setIsEditedZoneAmount(value);
        },
        [isEdited, isEditedZone, isEditedCityAmount, isEditedZoneAmount]
    );

    const handleModeChange = React.useCallback(
        (mode: DeliveryMode) => {
            if (anyFormEdited) {
                pendingActionRef.current = () => {
                    resetAllEdited();
                    resetAddFlags();
                    setDeliveryMode(mode);
                    updateDeliveryFlags(mode);
                };
                setShowConfirmation(true);
            } else {
                resetAddFlags();
                setDeliveryMode(mode);
                updateDeliveryFlags(mode);
            }
        },
        [anyFormEdited, resetAllEdited, updateDeliveryFlags, resetAddFlags]
    );

    return (
        <React.Fragment>
            <StyledH2
                style={{
                    font: "normal normal 400 27px/41px Segoe UI",
                    color: "#000000",
                    opacity: "1",
                }}
                className="ml-5 pt-3"
            >
                {t("delivery management")}
            </StyledH2>
            <Card className="ml-1" style={{ borderRightWidth: "unset" }}>
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        padding: "15px 20px",
                        gap: "10px 10px",
                    }}
                >
                    {RADIO_OPTIONS.map((option) => (
                        <Label
                            key={option.mode}
                            className="d-flex align-items-center mb-0 ml-3"
                            style={{
                                whiteSpace: "nowrap",
                                cursor: "pointer",
                                gap: "6px",
                            }}
                        >
                            <Input
                                type="radio"
                                name="radio1"
                                checked={deliveryMode === option.mode}
                                onChange={() => handleModeChange(option.mode)}
                                onCheckColor={"#6F763F"}
                                style={{ marginTop: 0 }}
                            />
                            <span
                                style={{
                                    font:
                                        "normal normal 400 14px/20px Segoe UI",
                                    color: "#000000",
                                    opacity: "1",
                                }}
                            >
                                {t(option.label)}
                            </span>
                        </Label>
                    ))}
                </div>
            </Card>
            {deliveryMode === "city" && (
                <Card
                    className="ml-1"
                    style={{ borderRightWidth: "unset", overflowY: "hidden" }}
                >
                    <City selectedRowShops={selectedRows} />
                </Card>
            )}
            {deliveryMode === "zone" && (
                <Card className="ml-1" style={{ borderRightWidth: "unset" }}>
                    <Zone selectedRowShops={selectedRows} />
                </Card>
            )}
            {deliveryMode === "zoneAmount" && (
                <Card className="ml-1" style={{ borderRightWidth: "unset" }}>
                    <ZoneAmount selectedRowShops={selectedRows} />
                </Card>
            )}
            {deliveryMode === "cityAmount" && (
                <Card
                    className="ml-1"
                    style={{ borderRightWidth: "unset", overflowY: "hidden" }}
                >
                    <CityAmount selectedRowShops={selectedRows} />
                </Card>
            )}

            {showConfirmation && (
                <ConfirmationUpdatingDataMessageModal
                    isModalOpened={showConfirmation}
                    setIsModalOpened={setShowConfirmation}
                    validationAction={() => setShowConfirmation(false)}
                    actions={() => pendingActionRef.current()}
                    setIsEdited={restoreAllEdited}
                />
            )}
        </React.Fragment>
    );
}
